import React, { useState } from 'react'
import Calculator from '@/components/Calculator'
import {TrendingUp, Shield, Clock, Info} from 'lucide-react'

export default function HomePage() {
  const [hoveredMaterial, setHoveredMaterial] = useState<string | null>(null)

  const materials = [
    { name: 'Asphalt Shingles', pricePerSqFt: '$3.50 - $5.50', lifespan: '15-30 years', popularity: 'Most Common', ecoFriendly: false },
    { name: 'Metal Roofing', pricePerSqFt: '$7.00 - $12.00', lifespan: '40-70 years', popularity: 'Growing', ecoFriendly: false },
    { name: 'Clay/Concrete Tile', pricePerSqFt: '$10.00 - $18.00', lifespan: '50-100 years', popularity: 'Premium', ecoFriendly: false },
    { name: 'Slate', pricePerSqFt: '$15.00 - $30.00', lifespan: '75-200 years', popularity: 'Luxury', ecoFriendly: false },
    { name: 'Recycled Shingles', pricePerSqFt: '$4.00 - $6.50', lifespan: '20-35 years', popularity: 'Eco-Friendly', ecoFriendly: true },
    { 
      name: 'Solar Roof Tiles', 
      pricePerSqFt: '$21.00 - $35.00', 
      lifespan: '25-30 years', 
      popularity: 'Energy-Saving', 
      ecoFriendly: true,
      tooltip: '💡 30% Federal Tax Credit • $1,000-$2,000/year energy savings • 10-15 year payback • Increases home value 4-6%'
    },
    { 
      name: 'Green/Living Roof', 
      pricePerSqFt: '$15.00 - $25.00', 
      lifespan: '30-50 years', 
      popularity: 'Sustainable', 
      ecoFriendly: true,
      tooltip: '🌿 State/local green building incentives • 20-30% cooling cost reduction • Stormwater tax rebates • 2-3x roof membrane lifespan'
    },
    { name: 'Recycled Metal', pricePerSqFt: '$8.00 - $14.00', lifespan: '50-75 years', popularity: 'Eco-Premium', ecoFriendly: true },
    { name: 'Composite Slate', pricePerSqFt: '$9.00 - $15.00', lifespan: '40-60 years', popularity: 'Eco-Luxury', ecoFriendly: true },
  ]

  const faqs = [
    {
      question: 'How do I calculate my roof square footage?',
      answer: 'Measure the length and width of each roof section and multiply them. Add all sections together. For pitched roofs, multiply by 1.3 to account for slope. Average homes range from 1,500-3,000 square feet.',
    },
    {
      question: 'What is the average square footage of a roof?',
      answer: 'The average residential roof in the US is approximately 1,700 square feet. Ranch-style homes typically have 1,500-2,000 sq ft, while two-story homes range from 2,000-3,000 sq ft.',
    },
    {
      question: 'What factors affect roof replacement cost?',
      answer: 'Key factors include: material choice, roof size and complexity, pitch/slope, number of layers to remove, local labor costs, permits, and accessibility. Geographic location can vary costs by 20-40%.',
    },
    {
      question: 'How accurate is this roof replacement cost calculator?',
      answer: 'Our roof quote calculator provides estimates based on national averages and material costs. Actual prices may vary by region, contractor, and specific project requirements. For precise pricing, request a free professional quote.',
    },
  ]

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Free Roofing Costs Calculator & Replacement Estimator
            </h1>
            <p className="text-xl sm:text-2xl text-blue-100 max-w-3xl mx-auto">
              Get an instant roof estimate in seconds. Calculate replacement costs, compare materials, and find trusted local contractors.
            </p>
          </div>

          <div className="flex justify-center">
            <Calculator />
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <TrendingUp className="text-orange-500 mb-3" size={40} />
              <h3 className="font-bold text-xl text-gray-900 mb-2">Current Roofing Labor Cost</h3>
              <p className="text-gray-600">Updated pricing data based on 2024 market rates and regional labor costs</p>
            </div>
            <div className="flex flex-col items-center">
              <Shield className="text-orange-500 mb-3" size={40} />
              <h3 className="font-bold text-xl text-gray-900 mb-2">Accurate Estimates</h3>
              <p className="text-gray-600">Industry-verified pricing for all major roofing materials and installation methods</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="text-orange-500 mb-3" size={40} />
              <h3 className="font-bold text-xl text-gray-900 mb-2">Instant Results</h3>
              <p className="text-gray-600">Get your roof replacement cost calculator results in under 30 seconds</p>
            </div>
          </div>
        </div>
      </section>

      {/* Material Comparison */}
      <section id="materials" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4">
            Roof Replacement Cost Calculator by Material
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Compare costs per square foot for different roofing materials. Use our instant roof estimate tool above to calculate your total project cost.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
              <thead className="bg-blue-800 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-bold">Material Type</th>
                  <th className="px-6 py-4 text-left font-bold">Cost per Sq Ft</th>
                  <th className="px-6 py-4 text-left font-bold">Lifespan</th>
                  <th className="px-6 py-4 text-left font-bold">Category</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((material, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      <div className="flex items-center gap-2">
                        {material.name}
                        {material.ecoFriendly && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                            🌱 Eco
                          </span>
                        )}
                        {material.tooltip && (
                          <div 
                            className="relative group inline-block"
                            onMouseEnter={() => setHoveredMaterial(material.name)}
                            onMouseLeave={() => setHoveredMaterial(null)}
                          >
                            <Info size={16} className="text-blue-600 cursor-help" aria-label="View ROI and tax credit information" />
                            {hoveredMaterial === material.name && (
                              <div className="absolute z-10 w-72 p-4 bg-white border-2 border-blue-600 rounded-lg shadow-2xl left-6 top-0 text-xs text-gray-800 leading-relaxed">
                                {material.tooltip}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-orange-600 font-bold">{material.pricePerSqFt}</td>
                    <td className="px-6 py-4 text-gray-700">{material.lifespan}</td>
                    <td className="px-6 py-4 text-gray-700">{material.popularity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-12">
            How to Calculate Roof Replacement Cost
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="font-bold text-xl text-gray-900 mb-3">Measure Your Roof</h3>
              <p className="text-gray-600">Calculate total square footage including all roof sections and pitch adjustments</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="font-bold text-xl text-gray-900 mb-3">Choose Material</h3>
              <p className="text-gray-600">Select from asphalt, metal, tile, or slate based on your budget and preferences</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-500 text-white rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="font-bold text-xl text-gray-900 mb-3">Get Instant Quote</h3>
              <p className="text-gray-600">Receive an immediate cost estimate and connect with local roofing contractors</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="font-bold text-lg text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  )
}
